import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'selectedAsset';
// In-tab sync: setStoredSelectedAsset dispatches this event so every
// useSelectedAsset() consumer in the same tab re-renders. The browser
// 'storage' event only fires for OTHER tabs, which is why we need our own.
const CHANGE_EVENT = 'helios:selected-asset-change';

export interface SelectedAsset {
  id: string;
  assetName: string;
  installedCapacityMw: number;
  region: string;
  operationalStatus: string;
  counterpartyShortName?: string;
  counterpartyName?: string;
}

export function getStoredSelectedAsset(): SelectedAsset | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SelectedAsset;
  } catch {
    return null;
  }
}

export function setStoredSelectedAsset(asset: SelectedAsset | null) {
  if (asset) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(asset));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

export function useSelectedAsset() {
  const [selectedAsset, setSelectedAssetState] = useState<SelectedAsset | null>(getStoredSelectedAsset);

  const setSelectedAsset = useCallback((asset: SelectedAsset | null) => {
    setStoredSelectedAsset(asset);
    setSelectedAssetState(asset);
  }, []);

  useEffect(() => {
    const refresh = () => setSelectedAssetState(getStoredSelectedAsset());
    const onStorage = (e: StorageEvent) => { if (e.key === STORAGE_KEY) refresh(); };
    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return { selectedAsset, setSelectedAsset };
}
